import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  PhoneAndroid,
  Computer,
  Tablet,
  DevicesOther,
  CheckCircle,
  Extension,
  Language,
  LaptopMac,
} from '@mui/icons-material';
import { Device } from '../services/api';
import { trackButtonClick, trackMessageAction } from '../utils/analytics';

interface ForwardDialogProps {
  open: boolean;
  onClose: () => void;
  devices: Device[];
  currentDeviceId: string | null;
  onSelectDevice: (device: Device) => void;
}

const ForwardDialog: React.FC<ForwardDialogProps> = ({
  open,
  onClose,
  devices,
  currentDeviceId,
  onSelectDevice,
}) => {
  const [selectedDevice, setSelectedDevice] = React.useState<Device | null>(null);

  // Reset selected device when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setSelectedDevice(null);
    }
  }, [open]);

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'android':
        return <PhoneAndroid />;
      case 'ios':
        return <PhoneAndroid />;
      case 'apple':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
          </svg>
        );
      case 'web':
        return <Language />;
      case 'chrome':
        return <Extension />;
      case 'tablet':
        return <Tablet />;
      default:
        return <DevicesOther />;
    }
  };

  const handleSelectDevice = (device: Device) => {
    setSelectedDevice(device);
  };

  const handleForward = () => {
    if (selectedDevice) {
      trackButtonClick('Forward Message', 'Forward Dialog');
      trackMessageAction('forward', selectedDevice.deviceType);
      onSelectDevice(selectedDevice);
      setSelectedDevice(null);
      onClose();
    }
  };

  const handleClose = () => {
    trackButtonClick('Cancel Forward', 'Forward Dialog');
    setSelectedDevice(null);
    onClose();
  };

  // Filter out current device
  const availableDevices = devices.filter(device => device.id !== currentDeviceId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forward Message To</DialogTitle>
      <DialogContent>
        {availableDevices.length === 0 ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              No other devices available to forward to
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {availableDevices.map((device) => (
              <ListItem
                key={device.id}
                disablePadding
                secondaryAction={
                  selectedDevice?.id === device.id && (
                    <CheckCircle sx={{ color: '#2196F3' }} />
                  )
                }
              >
                <ListItemButton
                  selected={selectedDevice?.id === device.id}
                  onClick={() => {
                    trackButtonClick('Select Device for Forward', 'Forward Dialog');
                    handleSelectDevice(device);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.25,
                    color: '#FFFFFF',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(33, 150, 243, 0.3)',
                      '&:hover': {
                        bgcolor: 'rgba(33, 150, 243, 0.4)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#FFFFFF' }}>{getDeviceIcon(device.deviceType)}</ListItemIcon>
                  <ListItemText
                    primary={device.name}
                    primaryTypographyProps={{ sx: { color: '#FFFFFF' } }}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Chip
                          label={device.deviceType}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            color: 'rgba(255, 255, 255, 0.8)'
                          }}
                        />
                      </Box>
                    }
                    secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.65)' } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose}
          sx={{ color: '#FFFFFF' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleForward}
          variant="contained"
          disabled={!selectedDevice}
          sx={{
            backgroundColor: '#2196F3',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#1976D2',
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          Forward
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardDialog;

